import React, { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import FilterModal from "./filtermodal";
import DeleteEntryModal from "./deleteentry";
import EditEntryModal from "./editinventory";
import { filtericon } from "./svgs";

dayjs.extend(customParseFormat);

const InventoryListView = ({
  setAddEntryModal,
  fetchinventry,
  setLoading,
  context,
  data = [],
}) => {
  // Pagination & Search
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Data handling
  const [backupData, setBackupData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Modals
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Filters
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const [colorFilterOpen, setColorFilterOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([
    "red",
    "yellow",
    "green",
  ]);

  /* ------------------ Backup original data ------------------ */
  useEffect(() => {
    if (data.length) {
      setBackupData(data);
      setFilteredData(data);
    }
  }, [data]);

  /* ------------------ Unique Types from BACKUP ------------------ */
  const uniqueTypes = useMemo(() => {
    return [...new Set(backupData.map((i) => i.context_name).filter(Boolean))];
  }, [backupData]);

  /* ------------------ Init type filter ------------------ */
  useEffect(() => {
    if (uniqueTypes.length) {
      setSelectedTypes(uniqueTypes);
    }
  }, [uniqueTypes]);

  /* ------------------ Quantity → Color logic ------------------ */
  const getQuantityColor = (total, lot) => {
    const diff = total - lot;
    if (diff <= 0) return "red";
    if (diff <= lot * 0.3) return "yellow";
    return "green";
  };

  /* ------------------ Apply filters on BACKUP ------------------ */
  useEffect(() => {
    const filtered = backupData.filter((item) => {
      const matchesSearch =
        item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ndc_number?.toLowerCase().includes(searchTerm.toLowerCase());

      const color = getQuantityColor(
        item.total_quantity,
        item.quantity_per_unit
      );

      return (
        matchesSearch &&
        selectedTypes.includes(item.context_name) &&
        selectedColors.includes(color)
      );
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedTypes, selectedColors, backupData]);

  /* ------------------ Pagination ------------------ */
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, filteredData.length);
  const currentData = filteredData.slice(startIndex, endIndex);

  return (
    <div className="p-4">
      <div className="rounded-xl border bg-white shadow p-4">
        {/* Header */}
        <div className="flex justify-between mb-4">
          <div className="text-sm font-medium">Inventory</div>
          <button
            onClick={() => setAddEntryModal(true)}
            className="flex items-center gap-1 bg-black text-white text-xs px-3 py-1 rounded"
          >
            <AddIcon fontSize="small" />
            Add New Item
          </button>
        </div>

        {/* Search */}
        <div className="flex justify-between mb-4">
          <div className="flex items-center gap-2 text-xs">
            Show
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(+e.target.value)}
              className="bg-gray-100 px-2 py-1 rounded"
            >
              {[5, 10, 15, 20].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center bg-gray-100 px-2 py-1 rounded">
              <SearchOutlinedIcon fontSize="small" />
              <input
                className="bg-transparent text-xs outline-none ml-1"
                placeholder="Search inventory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={() => {
                fetchinventry();
                setSearchTerm("");
              }}
            >
              <SyncOutlinedIcon fontSize="small" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto">
          <table className="w-full text-xs text-center">
            <thead>
              <tr>
                {[
                  "#",
                  "Date",
                  "Item Name",
                  "NDC Number",
                  "Quantity",
                  "Qty In Lot",
                  "Type",
                  "Actions",
                ].map((head) => (
                  <th key={head} className="border-y px-3 py-2">
                    <div className="flex justify-center items-center gap-1">
                      {head}
                      {head === "Quantity" && (
                        <span
                          className="cursor-pointer"
                          onClick={() => setColorFilterOpen(true)}
                        >
                          {filtericon}
                        </span>
                      )}
                      {head === "Type" && (
                        <span
                          className="cursor-pointer"
                          onClick={() => setTypeFilterOpen(true)}
                        >
                          {filtericon}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {currentData.map((item, idx) => {
                const color = getQuantityColor(
                  item.total_quantity,
                  item.quantity_per_unit
                );

                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="border-y px-3 py-2">
                      {startIndex + idx + 1}
                    </td>
                    <td className="border-y px-3 py-2">
                      {dayjs(item.created_on).format("MM-DD-YYYY")}
                    </td>
                    <td className="border-y px-3 py-2">{item.item_name}</td>
                    <td className="border-y px-3 py-2">{item.ndc_number}</td>
                    <td className="border-y px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded-full font-medium
                          ${color === "red" && "bg-red-100 text-red-700"}
                          ${color === "yellow" && "bg-yellow-100 text-yellow-700"}
                          ${color === "green" && "bg-green-100 text-green-700"}
                        `}
                      >
                        {item.total_quantity}
                      </span>
                    </td>
                    <td className="border-y px-3 py-2">
                      {item.quantity_per_unit}
                    </td>
                    <td className="border-y px-3 py-2">{item.context_name}</td>
                    <td className="border-y px-3 py-2">
                      <div className="flex justify-center gap-2">
                        <EditOutlinedIcon
                          className="cursor-pointer text-blue-600"
                          onClick={() => {
                            setSelectedItem(item);
                                 console.log(item,'item')
                            setEditModal(true);
                          }}
                        />
                        <DeleteOutlineOutlinedIcon
                          className="cursor-pointer text-red-600"
                          onClick={() => {
                            setSelectedItem(item);
                       
                            setDeleteModal(true);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between pt-4 text-xs">
          <span>
            Showing {startIndex + 1} to {endIndex} of {filteredData.length}
          </span>
          <div className="flex gap-2">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
              <KeyboardArrowLeftOutlinedIcon fontSize="small" />
            </button>
            <span className="bg-black text-white px-3 py-1 rounded">
              {currentPage}
            </span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
              <KeyboardArrowRightOutlinedIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Modals */}
      {typeFilterOpen && (
        <FilterModal
          options={uniqueTypes.map((t) => ({ label: t, value: t }))}
          selected={selectedTypes}
          setSelected={setSelectedTypes}
          onClose={() => setTypeFilterOpen(false)}
        />
      )}

      {colorFilterOpen && (
        <FilterModal
          options={[
            { label: "Red", value: "red" },
            { label: "Yellow", value: "yellow" },
            { label: "Green", value: "green" },
          ]}
          selected={selectedColors}
          setSelected={setSelectedColors}
          onClose={() => setColorFilterOpen(false)}
        />
      )}

      {/* Edit & Delete */}
      {editModal && selectedItem && (
        <EditEntryModal
          selectedItem={selectedItem}
          setEditModal={setEditModal}
          fetchInventory={fetchinventry}
          setLoading={setLoading}
        />
      )}

      {deleteModal && selectedItem && (
        <DeleteEntryModal
          selectedItem={selectedItem}
          setDeleteModal={setDeleteModal}
          fetchInventory={fetchinventry}
          setLoading={setLoading}
        />
      )}
    </div>
  );
};

export default InventoryListView;
